/**
 * An estimated dollar figure with its caveat: "≈ $0.004" plus a tooltip
 * explaining it is estimated from the model's published price at the time of
 * each call. Shared by every cost surface (runs, usage meter, card detail).
 *
 * - `usd === undefined` renders nothing (the BE did not send the field).
 * - `usd === null` renders "cost not known" — never "$0".
 * - `partial` marks a total that leaves out calls with no known price:
 *   "≈ $0.06+", with the reason in the tooltip. A partial total of zero has
 *   nothing to show, so it reads "cost not known" too.
 *
 * The figure is focusable so the explanation is reachable from the keyboard.
 */

import { useTranslation } from 'react-i18next'

import { Tooltip } from '@/components/Tooltip'
import { formatEstimatedUsd } from '@/lib/format-cost'

interface EstimatedCostProps {
  usd: number | null | undefined
  partial?: boolean
  className?: string
}

export function EstimatedCost({ usd, partial = false, className }: EstimatedCostProps) {
  const { t, i18n } = useTranslation('common')
  if (usd === undefined) return null

  if (usd === null || (partial && usd <= 0)) {
    return <span className={className}>{t('cost.notKnown')}</span>
  }

  const text = formatEstimatedUsd(usd, i18n.language || 'en', { partial })
  const hint = partial
    ? `${t('cost.estimateHint')} ${t('cost.partialHint')}`
    : t('cost.estimateHint')

  return (
    <Tooltip content={hint}>
      <span
        // biome-ignore lint/a11y/noNoninteractiveTabindex: focus opens the estimate tooltip
        tabIndex={0}
        className={`cursor-help whitespace-nowrap rounded-sm underline decoration-dotted underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-fg-subtle ${className ?? ''}`}
      >
        {text}
      </span>
    </Tooltip>
  )
}
